import React from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import services from '@services'
import Panel from '@components/common/Panel'
import Table from '@components/common/Table'
import YSpacing from '@components/common/YSpacing'
import { Button } from '@components/common/form'
import { DinerProfile } from '@types'

const { RestService, UIService } = services

interface Props {
  clientSettingsId: string
  clientSettingsName: string
  accountId: string
  currentHqId: number
}

const LinkedCateringMenusTab: React.FC<Props> = ({
  clientSettingsId,
  accountId,
  currentHqId,
}) => {
  const queryClient = useQueryClient()
  const linkedDinerProfilesKey = [
    'linkedDinerProfiles',
    clientSettingsId,
  ] as const

  const invalidate = () => {
    queryClient.invalidateQueries(linkedDinerProfilesKey)
    queryClient.invalidateQueries([
      'availableDinerProfiles',
      accountId,
      currentHqId,
    ])
  }

  const linkedDinerProfilesQuery = useQuery(
    linkedDinerProfilesKey,
    async (): Promise<DinerProfile[]> => {
      const res = await RestService.get(
        `/group-orders/client-settings/${clientSettingsId}/diner-profiles`,
      )

      return res ?? []
    },
    { enabled: !!clientSettingsId },
  )

  const linkedProfiles = linkedDinerProfilesQuery.data ?? []
  const linkedProfileIds = new Set(linkedProfiles.map((p) => p.id))

  const availableDinerProfilesQuery = useQuery(
    ['availableDinerProfiles', accountId, currentHqId],
    async (): Promise<DinerProfile[]> => {
      //TODO - check diner-profiles for the specific hqId
      const res = await RestService.get('/accounts/diner-profiles', {
        accountId,
        limit: 200,
      })

      return res ?? []
    },
    { enabled: !!accountId && !!currentHqId },
  )

  const availableDinerProfiles = availableDinerProfilesQuery.data ?? []
  const areQueriesLoading =
    linkedDinerProfilesQuery.isLoading || availableDinerProfilesQuery.isLoading

  const linkMutation = useMutation(
    async (dinerProfileId: string) => {
      await RestService.post(
        `/group-orders/client-settings/${clientSettingsId}/diner-profiles`,
        { dinerProfileIds: [dinerProfileId] },
      )
    },
    {
      onSuccess: invalidate,
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to link diner profile.',
        ),
    },
  )

  const unlinkMutation = useMutation(
    async (dinerProfileId: string) => {
      await RestService.delete(
        `/group-orders/client-settings/${clientSettingsId}/diner-profile/${dinerProfileId}`,
      )
    },
    {
      onSuccess: invalidate,
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to unlink diner profile.',
        ),
    },
  )

  const isMutating = linkMutation.isLoading || unlinkMutation.isLoading

  const onToggleLink = async (profile: DinerProfile) => {
    const isLinked = linkedProfileIds.has(profile.id)
    const action = isLinked ? 'unlink' : 'link'
    const confirmed = await UIService?.ConfirmationModal.show({
      text: `Are you sure you want to ${action} "${profile.name}"?`,
    })
    if (!confirmed || !profile.id) {
      return
    }
    if (isLinked) {
      unlinkMutation.mutate(profile.id)
    } else {
      linkMutation.mutate(profile.id)
    }
  }

  return (
    <div>
      <YSpacing height="20px" />
      <Panel width="100%" maxWidth="900px" heading="Link Catering Menus">
        <p style={{ fontSize: '13px', color: '#444', marginBottom: '16px' }}>
          Linking catering menus controls which diner profiles are available on
          the Group Order website for this location. When a Group Order user
          also has access to catering menus, linking diner profiles to a
          location enables those catering menus to be displayed on the Group
          Order website.
        </p>
        {areQueriesLoading && <p>Loading...</p>}
        {!areQueriesLoading && availableDinerProfiles.length === 0 && (
          <p>No diner profiles available for this account.</p>
        )}
        {!areQueriesLoading && availableDinerProfiles.length > 0 && (
          <Table
            headings={[
              'Name',
              'Meal Type',
              'Address',
              'Contact',
              'Headcount',
              'Link',
            ]}
          >
            {availableDinerProfiles.map((profile) => {
              const isLinked = linkedProfiles.some((p) => p.id === profile.id)
              const address = profile.address
                ? [
                    profile.address.line1,
                    profile.address.city,
                    profile.address.state,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : '—'
              const contact =
                profile.contact?.name || profile.contact?.email || '—'

              return (
                <tr key={profile.id}>
                  <td>{profile.name}</td>
                  <td>{profile.mealType}</td>
                  <td>{address}</td>
                  <td>{contact}</td>
                  <td>{profile.headcount}</td>
                  <td>
                    <Button
                      label={isLinked ? 'Unlink' : 'Link'}
                      backgroundColor={isLinked ? '#e8264b' : undefined}
                      onClick={() => onToggleLink(profile)}
                      disabled={isMutating}
                      padding="6px 16px"
                    />
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
      </Panel>
    </div>
  )
}

export default LinkedCateringMenusTab
